import { createElement, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { toast } from 'sonner'

import { useGetPayrollAdpConfig, usePostPayrollAdpConnectAuthorizeUri } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import { AdpIntegrationModal } from '../payroll/AdpIntegrationModal'
import { type AdpIntegrationStep } from '../payroll/types'

interface UseAdpIntegrationProps {
  onSuccessUrl: string;
  onFailureUrl?: string;
}

export function useAdpIntegration ({ onSuccessUrl, onFailureUrl }: UseAdpIntegrationProps) {
  const { assign } = useWindowLocation()

  const [providerIdToOpen, setProviderIdToOpen] = useState<string | undefined>(undefined)
  const [currentStep, setCurrentStep] = useState<AdpIntegrationStep>('info')
  const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null)
  const [modalRoot, setModalRoot] = useState<any>(null)

  // Fetch ADP configuration from the API
  const { data: adpConfig } = useGetPayrollAdpConfig()

  const authorizeUriMutation = usePostPayrollAdpConnectAuthorizeUri({
    request: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  })

  const startConnection = async (providerId: string) => {
    setProviderIdToOpen(providerId)
    setCurrentStep('info')
  }

  const handleStartMarketplaceSubscription = () => {
    const marketplaceUrl = adpConfig?.marketplace_url

    if (!marketplaceUrl) {
      toast.error('Unable to load ADP connection configuration. Please try again.')
      return
    }

    // Open marketplace in new window
    const marketplaceWindow = window.open(
      marketplaceUrl,
      'adp-marketplace',
      'width=800,height=700,scrollbars=yes,resizable=yes'
    )

    if (!marketplaceWindow || marketplaceWindow.closed || typeof marketplaceWindow.closed === 'undefined') {
      toast.error('Please allow popups for this site to access the ADP Marketplace or copy the URL and paste it into a new tab.')
    }
  }

  const handleStepChange = (step: AdpIntegrationStep) => {
    setCurrentStep(step)
  }

  const handleStartOAuthAuthorization = async () => {
    try {
      // Set step to redirecting to show loading state
      setCurrentStep('redirecting')

      // Start OAuth authorization flow
      authorizeUriMutation.mutate(
        { data: { on_success: onSuccessUrl, on_failure: onFailureUrl } },
        {
          onSuccess: (response: any) => {
            // Perform full-page redirect to ADP's OAuth authorization URL
            assign(response.authorization_url)
          },
          onError: () => {
            // Reset step back to oauth on error
            setCurrentStep('oauth')
            toast.error('Failed to start ADP OAuth flow. Please ensure you have subscribed to the Thatch app in the ADP Marketplace and try again.')
          }
        }
      )
    } catch {
      // Reset step back to oauth on error
      setCurrentStep('oauth')
      toast.error('Failed to start ADP OAuth flow. Please try again.')
    }
  }

  const handleCloseModal = () => {
    closeModal()
    cleanup()
  }

  const renderModal = () => {
    if (!modalContainer || !modalRoot) return

    modalRoot.render(
      createElement(AdpIntegrationModal, {
        isOpen: true,
        onClose: handleCloseModal,
        onStartMarketplaceSubscription: handleStartMarketplaceSubscription,
        onStartOAuthAuthorization: handleStartOAuthAuthorization,
        onStepChange: handleStepChange,
        marketplaceUrl: adpConfig?.marketplace_url || '',
        currentStep,
      })
    )
  }

  const closeModal = () => {
    if (modalRoot) {
      modalRoot.unmount()
      setModalRoot(null)
    }
    if (modalContainer && document.body.contains(modalContainer)) {
      document.body.removeChild(modalContainer)
      setModalContainer(null)
    }
  }

  // We want this to behave similarly to the third-party modals so we can use the existing interface
  const openModal = () => {
    if (providerIdToOpen) {
      // Create modal container
      const container = document.createElement('div')
      container.id = 'adp-modal-portal'
      document.body.appendChild(container)
      setModalContainer(container)

      // Create React root and render modal
      const root = createRoot(container)
      setModalRoot(root)

      // Render the modal
      root.render(
        createElement(AdpIntegrationModal, {
          isOpen: true,
          onClose: handleCloseModal,
          onStartMarketplaceSubscription: handleStartMarketplaceSubscription,
          onStartOAuthAuthorization: handleStartOAuthAuthorization,
          onStepChange: handleStepChange,
          marketplaceUrl: adpConfig?.marketplace_url || '',
          currentStep,
        })
      )
    }
  }

  const cleanup = () => {
    setProviderIdToOpen(undefined)
    closeModal()
  }

  // Re-render the modal whenever the step changes so the UI stays in sync
  useEffect(() => {
    if (modalRoot) {
      renderModal()
    }
  }, [currentStep, modalRoot])

  return {
    startConnection,
    openModal,
    providerIdToOpen,
    setProviderIdToOpen,
    currentStep,
    cleanup,
    modalContainer,
  }
}
